import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Icon,
  Input,
  Link,
  Select,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/core';
import { apiV1 } from '../../api/api';
import {
  COMPANY_DOCUMENT_CATEGORY_LABELS,
  getCompanyDocumentsCompletion,
  isCompanyDocumentCategoryComplete,
  isSharedCompanyDocumentCategory,
} from './documentCompletion';
import FileUploadError from '../FileUploadError';
import {
  getUploadError,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  UPLOAD_SIZE_ERROR,
} from '../../helpers/fileUpload';
import { companyDocumentFileUrl } from '../../helpers/documentFiles';

const FILE_ACCEPT = {
  site_photos: 'image/jpeg,image/png,image/webp',
  reception_report: 'application/pdf',
  company_invoice: 'application/pdf',
  signed_discharge_letter: 'application/pdf',
};

const REVIEW_STATE_LABELS = {
  under_review: 'En cours de validation par BelAvie',
  valid: 'Validée par BelAvie',
  incorrect: 'Refusée par BelAvie',
};

const REVIEW_STATE_COLORS = {
  under_review: 'orange.900',
  valid: 'green.700',
  incorrect: 'coral.700',
};

const ReviewState = ({ field }) => {
  if (!field || !REVIEW_STATE_LABELS[field.state]) return null;

  return (
    <Text mt={1} fontSize="sm" fontWeight="semibold" color={REVIEW_STATE_COLORS[field.state]}>
      {REVIEW_STATE_LABELS[field.state]}
      {field.state === 'incorrect' && field.reviewComment ? ` : ${field.reviewComment}` : ''}
    </Text>
  );
};

const categoryDocuments = (categories, category) =>
  categories.find(item => item.category === category)?.documents || [];

const filename = document =>
  document.originalFilename || document.sharepointFilename || document.filename || 'Consulter';

const downloadLink = document => document.fileDownloadLink || document.downloadLink;

const schemaFields = schema => {
  if (Array.isArray(schema)) return schema;
  return schema?.fields || [];
};

const DocumentLinks = ({ documents, linkFor = downloadLink }) => {
  if (!documents.length) return null;

  return (
    <Box as="ul" pl={5} mt={2}>
      {documents.map((document, index) => (
        <Text as="li" key={document.id || `${filename(document)}-${index}`} fontSize="sm">
          {linkFor(document) ? (
            <Link href={linkFor(document)} isExternal color="coral.900">
              {filename(document)}
            </Link>
          ) : (
            filename(document)
          )}
        </Text>
      ))}
    </Box>
  );
};

const UploadZone = ({ prompt, children }) => (
  <Flex
    mt={3}
    p={2}
    border="1px dashed"
    borderColor="lightGray3"
    rounded="md"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    gridGap={2}
    data-document-upload-zone
  >
    <Box>
      <Text fontSize="sm">{prompt}</Text>
      <Text fontSize="xs" color="semiDarkGray.600">
        {MAX_UPLOAD_SIZE_MB} Mo maximum par fichier
      </Text>
    </Box>
    {children}
  </Flex>
);

const FileUpload = ({ category, phase, pending, disabled, status, error, onUpload }) => {
  const inputId = `company-document-${category}-${phase || 'file'}`;
  const multiple = Boolean(FILE_ACCEPT[category]);
  const isPhoto = category === 'site_photos';

  return (
    <>
      <UploadZone
        prompt={
          isPhoto
            ? 'Sélectionnez une ou plusieurs images JPG, PNG ou WebP'
            : 'Sélectionnez un ou plusieurs PDF'
        }
      >
        <Input
          id={inputId}
          type="file"
          accept={FILE_ACCEPT[category]}
          multiple={multiple}
          isDisabled={disabled}
          display="none"
          onChange={event => {
            const input = event.target;
            const files = Array.from(input.files || []);
            if (!disabled && files.length) onUpload(category, files, phase);
            input.value = '';
          }}
        />
        <Button
          as="label"
          variantColor="neutral"
          htmlFor={inputId}
          size="sm"
          bg="coral.900"
          color="white"
          _hover={{ bg: 'semiDarkGray.900' }}
          cursor="pointer"
          isLoading={pending}
          isDisabled={disabled}
          loadingText="Envoi…"
        >
          {isPhoto ? 'Choisir des photos' : 'Choisir des fichiers'}
        </Button>
      </UploadZone>
      {status && (
        <Text role="status" mt={2} fontSize="sm">
          {status}
        </Text>
      )}
      <FileUploadError error={error} whiteSpace="pre-line" />
    </>
  );
};

const fieldValue = (payload, field) => payload?.[field.key] ?? '';

const DischargeLetterForm = ({ schema, document, pending, canWrite, onSubmit, onDownload }) => {
  const fields = schemaFields(schema);
  const downloadButtonRef = useRef();
  const [values, setValues] = useState(() => document?.payload || schema.defaults || {});
  const hasUnsavedChanges =
    document?.received &&
    fields.some(field => fieldValue(values, field) !== fieldValue(document.payload, field));

  useEffect(() => {
    setValues(document?.payload || schema.defaults || {});
  }, [document, schema]);

  if (!fields.length) {
    return (
      <Box mt={3} p={3} bg="lightGray" border="1px dashed" borderColor="lightGray3" rounded="md">
        <Text color="semiDarkGray.600" fontSize="sm">
          Modèle en attente de validation.
        </Text>
      </Box>
    );
  }

  const updateValue = (field, value) => {
    setValues(previous => ({ ...previous, [field.key]: value }));
  };

  const renderFieldInput = (field, type, id, value) => {
    if (type === 'textarea') {
      return (
        <Textarea
          id={id}
          value={value}
          isDisabled={pending || !canWrite}
          onChange={event => updateValue(field, event.target.value)}
        />
      );
    }

    if (type === 'select') {
      return (
        <Select
          id={id}
          value={value}
          isDisabled={pending || !canWrite}
          onChange={event => updateValue(field, event.target.value)}
        >
          <option value="">Sélectionner</option>
          {(field.options || []).map(option => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </Select>
      );
    }

    return (
      <Input
        id={id}
        type={type}
        value={value}
        isDisabled={pending || !canWrite}
        onChange={event => updateValue(field, event.target.value)}
      />
    );
  };

  return (
    <Box
      as="form"
      mt={3}
      onSubmit={event => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      <Text mb={3} color="semiDarkGray.600" fontSize="sm">
        À remplir lorsque des éléments prévus n’ont pas été réalisés. Enregistrez la lettre, puis
        téléchargez-la pour recueillir les signatures du bénéficiaire et de BelAvie.
      </Text>
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
        {fields.map(field => {
          const key = field.key || field.name;
          const type = field.type || 'text';
          const value = fieldValue(values, { ...field, key });
          const id = `discharge-letter-${key}`;

          if (type === 'boolean') {
            return (
              <Checkbox
                key={key}
                id={id}
                isChecked={Boolean(value)}
                isDisabled={pending || !canWrite}
                onChange={event => updateValue(field, event.target.checked)}
                mb={3}
              >
                {field.label}
              </Checkbox>
            );
          }

          return (
            <FormControl
              key={key}
              mb={1}
              gridColumn={type === 'textarea' ? '1 / -1' : undefined}
              isRequired={Boolean(field.required)}
            >
              <FormLabel htmlFor={id}>{field.label}</FormLabel>
              {renderFieldInput(field, type, id, value)}
            </FormControl>
          );
        })}
      </Grid>
      <Flex mt={3} flexWrap="wrap" gridGap={2}>
        {canWrite && (
          <Button
            type="submit"
            variantColor="neutral"
            size="sm"
            bg="coral.900"
            color="white"
            _hover={{ bg: 'semiDarkGray.900' }}
            isLoading={pending}
          >
            Enregistrer la lettre de décharge
          </Button>
        )}
        {document?.received && (
          <Button
            ref={downloadButtonRef}
            type="button"
            size="sm"
            variant="outline"
            onClick={async () => {
              await onDownload();
              requestAnimationFrame(() => downloadButtonRef.current?.focus());
            }}
            isDisabled={pending || hasUnsavedChanges}
          >
            Télécharger pour signature
          </Button>
        )}
      </Flex>
      {hasUnsavedChanges && (
        <Text mt={2} fontSize="sm" color="semiDarkGray.600">
          Enregistrez vos modifications avant de télécharger la lettre.
        </Text>
      )}
    </Box>
  );
};

export const CompanyDocumentsPanel = ({
  companiesQuoteGroupId,
  canWrite = false,
  canFillDischargeLetter = canWrite,
  quoteUpload = null,
  showHeader = true,
  onCompletionChange,
  sharedDocuments = null,
}) => {
  const [categories, setCategories] = useState([]);
  const [fields, setFields] = useState([]);
  const [pending, setPending] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploadKey, setUploadKey] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const uploadInProgress = useRef(false);
  const documentsRegionRef = useRef(null);
  const retryButtonRef = useRef(null);
  const restoreFocusAfterRetryRef = useRef(false);
  const [dischargeLetterSchema, setDischargeLetterSchema] = useState({ fields: [] });
  const [dischargeLetterPending, setDischargeLetterPending] = useState(false);
  const [dischargeLetterError, setDischargeLetterError] = useState(null);
  const [canRequestDischarge, setCanRequestDischarge] = useState(false);

  const loadDocuments = ({ restoreFocus = false } = {}) => {
    if (restoreFocus) restoreFocusAfterRetryRef.current = true;
    setPending(true);
    setLoadError(false);
    if (onCompletionChange) onCompletionChange(null);
    return apiV1
      .get(`/companies_quote_groups/${companiesQuoteGroupId}/documents`)
      .then(({ data }) => {
        if (!Array.isArray(data.documents)) throw new Error('Documents indisponibles');
        setCategories(data.documents);
        setFields(Array.isArray(data.fields) ? data.fields : []);
        setCanRequestDischarge(Boolean(data.canRequestDischarge));
        if (onCompletionChange) onCompletionChange(getCompanyDocumentsCompletion(data.documents));
        setDischargeLetterSchema(
          data.dischargeLetterSchema || data.discharge_letter_schema || { fields: [] },
        );
      })
      .catch(() => {
        setLoadError(true);
        if (onCompletionChange) {
          onCompletionChange({
            state: 'unavailable',
            description: 'État des documents entreprise indisponible.',
          });
        }
      })
      .finally(() => setPending(false));
  };

  useEffect(() => {
    if (!restoreFocusAfterRetryRef.current || pending) return undefined;

    restoreFocusAfterRetryRef.current = false;
    const target = loadError ? retryButtonRef.current : documentsRegionRef.current;
    if (!target || typeof target.focus !== 'function') return undefined;

    const timeoutId = setTimeout(() => target.focus(), 0);
    return () => clearTimeout(timeoutId);
  }, [categories, loadError, pending]);

  useEffect(() => {
    if (companiesQuoteGroupId) loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesQuoteGroupId]);

  const upload = async (category, selectedFiles, photoPhase) => {
    if (uploadInProgress.current) return;
    const files = selectedFiles;
    if (!files.length) return;
    const key = `${category}-${photoPhase || 'file'}`;
    const failures = [];
    let uploaded = 0;
    const isPhoto = category === 'site_photos';
    const label = isPhoto ? 'photo' : 'document';
    uploadInProgress.current = true;
    setUploadError(null);
    setUploadStatus(null);
    setUploadKey(key);

    try {
      await files.reduce(
        (previous, file, index) =>
          previous.then(async () => {
            setUploadStatus({
              key,
              message: `Envoi ${isPhoto ? 'de la photo' : 'du document'} ${index + 1} sur ${
                files.length
              }…`,
            });
            let failure;
            if (file.size > MAX_UPLOAD_SIZE_BYTES) {
              failure = UPLOAD_SIZE_ERROR;
            } else {
              const formData = new FormData();
              formData.append('company_document[category]', category);
              if (photoPhase) formData.append('company_document[photo_phase]', photoPhase);
              formData.append('company_document[file]', file, file.name);
              try {
                await apiV1.post(
                  `/companies_quote_groups/${companiesQuoteGroupId}/documents`,
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                uploaded += 1;
              } catch (uploadFailure) {
                failure = getUploadError(uploadFailure);
              }
            }
            if (failure) failures.push(`${file.name} : ${failure}`);
          }),
        Promise.resolve(),
      );
      if (uploaded) await loadDocuments();
      const participle = isPhoto ? 'ajoutée' : 'ajouté';
      setUploadStatus({
        key,
        message: `${uploaded} ${label}${uploaded > 1 ? 's' : ''} ${participle}${
          uploaded > 1 ? 's' : ''
        } sur ${files.length}.`,
      });
      if (failures.length) setUploadError({ key, message: failures.join('\n') });
    } finally {
      uploadInProgress.current = false;
      setUploadKey(null);
    }
  };

  const submitDischargeLetter = payload => {
    setDischargeLetterPending(true);
    setDischargeLetterError(null);
    apiV1
      .post(`/companies_quote_groups/${companiesQuoteGroupId}/documents`, {
        company_document: { category: 'discharge_letter', payload },
      })
      .then(loadDocuments)
      .catch(failure => setDischargeLetterError(getUploadError(failure)))
      .finally(() => setDischargeLetterPending(false));
  };

  const requestDischargeLetter = required => {
    setDischargeLetterPending(true);
    setDischargeLetterError(null);
    apiV1
      .patch(`/companies_quote_groups/${companiesQuoteGroupId}/documents/discharge_requirement`, {
        required,
      })
      .then(loadDocuments)
      .catch(failure => setDischargeLetterError(getUploadError(failure)))
      .finally(() => setDischargeLetterPending(false));
  };

  const downloadDischargeLetter = async document => {
    setDischargeLetterPending(true);
    setDischargeLetterError(null);
    try {
      const {
        data,
      } = await apiV1.get(
        `/companies_quote_groups/${companiesQuoteGroupId}/documents/${document.id}/export`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(data);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `lettre-decharge-${companiesQuoteGroupId}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      setDischargeLetterError('La lettre n’a pas pu être téléchargée. Réessayez.');
    } finally {
      setDischargeLetterPending(false);
    }
  };

  const companyFileLink = document => companyDocumentFileUrl(companiesQuoteGroupId, document);

  if (pending && categories.length === 0) {
    return <Spinner aria-label="Chargement des documents de l’entreprise" />;
  }

  if (loadError && categories.length === 0) {
    return (
      <Flex alignItems="center" flexWrap="wrap" gridGap={3}>
        <Text role="alert" color="red.500">
          Les documents de l’entreprise sont indisponibles.
        </Text>
        <Button
          ref={retryButtonRef}
          type="button"
          size="sm"
          onClick={() => loadDocuments({ restoreFocus: true })}
          isLoading={pending}
        >
          Réessayer
        </Button>
      </Flex>
    );
  }

  return (
    <Box
      ref={documentsRegionRef}
      role="region"
      aria-label="Documents de l’entreprise"
      aria-busy={pending}
      tabIndex={-1}
    >
      {showHeader && (
        <Box bg="darkGray" color="white" rounded="md" px={4} py={3} mb={3}>
          <Heading as="h4" fontSize="md" color="white">
            Documents de l’entreprise
          </Heading>
          <Text mt={1} color="rgba(255, 255, 255, 0.72)" fontSize="sm">
            Les pièces liées à l’entreprise sont regroupées ici.
          </Text>
        </Box>
      )}
      {loadError && (
        <Flex alignItems="center" flexWrap="wrap" gridGap={3} mb={3}>
          <Text role="alert" color="red.500" fontSize="sm">
            L’état des documents de l’entreprise n’a pas pu être actualisé. Les données affichées
            peuvent être obsolètes.
          </Text>
          <Button
            ref={retryButtonRef}
            type="button"
            size="sm"
            onClick={() => loadDocuments({ restoreFocus: true })}
            isLoading={pending}
          >
            Réessayer
          </Button>
        </Flex>
      )}
      {Object.entries(COMPANY_DOCUMENT_CATEGORY_LABELS).map(([category, label]) => {
        if (isSharedCompanyDocumentCategory(categories, category)) return null;
        const fieldFor = key => fields.find(field => field.key === key);
        const documents = categoryDocuments(categories, category);
        const receivedDocuments = documents.filter(document => document.received === true);
        const isComplete = isCompanyDocumentCategoryComplete(category, documents);
        const isOptional = categories.find(item => item.category === category)?.required === false;
        let completionLabel = isOptional ? 'Selon la situation' : 'À fournir';
        if (isComplete) completionLabel = 'Déposé';
        if (isComplete && category === 'discharge_letter')
          completionLabel = 'Formulaire enregistré';
        const statusColor = isComplete ? 'green.700' : 'semiDarkGray.600';

        return (
          <Box
            key={category}
            data-document-category={category}
            mb={3}
            p={3}
            bg="white"
            border="1px solid"
            borderColor="lightGray3"
            borderLeft="4px solid"
            borderLeftColor={statusColor}
            rounded="md"
            boxShadow="0 2px 8px rgba(46, 73, 112, 0.08)"
          >
            <Box>
              <Flex alignItems="center" gridGap={2}>
                {isComplete ? (
                  <Icon
                    name="check-circle"
                    color={statusColor}
                    aria-label="Déposé"
                    title="Déposé"
                  />
                ) : (
                  <Text
                    as="span"
                    role="img"
                    aria-label={isOptional ? 'Selon la situation' : 'À fournir'}
                    title={isOptional ? 'Selon la situation' : 'À fournir'}
                    color={statusColor}
                  >
                    —
                  </Text>
                )}
                <Text fontWeight="semibold">{label}</Text>
              </Flex>
              <Text mt={1} fontSize="xs" color={statusColor} fontWeight="semibold">
                {completionLabel}
              </Text>
            </Box>
            <Box>
              {category === 'site_photos' ? (
                <>
                  {['before', 'after'].map(phase => (
                    <Box key={phase} mt={2}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {phase === 'before' ? 'Avant chantier' : 'Après chantier'}
                      </Text>
                      <DocumentLinks
                        documents={receivedDocuments.filter(item => item.photoPhase === phase)}
                        linkFor={companyFileLink}
                      />
                      <ReviewState field={fieldFor(`site_photos_${phase}`)} />
                      {canWrite && !fieldFor(`site_photos_${phase}`)?.locked && (
                        <FileUpload
                          category={category}
                          phase={phase}
                          pending={uploadKey === `${category}-${phase}`}
                          disabled={Boolean(uploadKey)}
                          status={
                            uploadStatus?.key === `${category}-${phase}`
                              ? uploadStatus.message
                              : null
                          }
                          error={
                            uploadError?.key === `${category}-${phase}` ? uploadError.message : null
                          }
                          onUpload={upload}
                        />
                      )}
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {category !== 'discharge_letter' && (
                    <DocumentLinks
                      documents={receivedDocuments}
                      linkFor={category === 'quote' ? downloadLink : companyFileLink}
                    />
                  )}
                  {category !== 'discharge_letter' && <ReviewState field={fieldFor(category)} />}
                  {category === 'quote' && canWrite && quoteUpload && (
                    <UploadZone prompt="Déposez un PDF ici">{quoteUpload}</UploadZone>
                  )}
                  {FILE_ACCEPT[category] && canWrite && !fieldFor(category)?.locked && (
                    <FileUpload
                      category={category}
                      pending={uploadKey === `${category}-file`}
                      disabled={Boolean(uploadKey)}
                      status={
                        uploadStatus?.key === `${category}-file` ? uploadStatus.message : null
                      }
                      error={uploadError?.key === `${category}-file` ? uploadError.message : null}
                      onUpload={upload}
                    />
                  )}
                  {category === 'discharge_letter' && (
                    <>
                      {canRequestDischarge && (
                        <Checkbox
                          mt={3}
                          isChecked={!isOptional}
                          isDisabled={dischargeLetterPending}
                          onChange={event => requestDischargeLetter(event.target.checked)}
                        >
                          Décharge demandée à l’entreprise
                        </Checkbox>
                      )}
                      <DischargeLetterForm
                        schema={dischargeLetterSchema}
                        document={receivedDocuments[0]}
                        pending={dischargeLetterPending}
                        canWrite={canFillDischargeLetter}
                        onSubmit={submitDischargeLetter}
                        onDownload={() => downloadDischargeLetter(receivedDocuments[0])}
                      />
                      {dischargeLetterError && (
                        <Text role="alert" color="red.500" mt={2}>
                          {dischargeLetterError}
                        </Text>
                      )}
                      <Box mt={4}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Lettre de décharge signée
                        </Text>
                        <DocumentLinks
                          documents={categoryDocuments(
                            categories,
                            'signed_discharge_letter',
                          ).filter(document => document.received === true)}
                          linkFor={companyFileLink}
                        />
                        <ReviewState field={fieldFor('discharge_letter')} />
                        {canWrite && !fieldFor('discharge_letter')?.locked && (
                          <FileUpload
                            category="signed_discharge_letter"
                            pending={uploadKey === 'signed_discharge_letter-file'}
                            disabled={Boolean(uploadKey)}
                            status={
                              uploadStatus?.key === 'signed_discharge_letter-file'
                                ? uploadStatus.message
                                : null
                            }
                            error={
                              uploadError?.key === 'signed_discharge_letter-file'
                                ? uploadError.message
                                : null
                            }
                            onUpload={upload}
                          />
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      })}
      {sharedDocuments &&
        categories.some(item => item.source === 'funding_documents' && item.available) && (
          <Box
            data-document-category="shared_anah_documents"
            mb={3}
            p={3}
            bg="white"
            border="1px solid"
            borderColor="lightGray3"
            rounded="md"
            boxShadow="0 2px 8px rgba(46, 73, 112, 0.08)"
          >
            {sharedDocuments}
          </Box>
        )}
    </Box>
  );
};

export default CompanyDocumentsPanel;
